import { fmtCompact, fmtChange, fmtMoney, fmtNumber, fmtPct, periodLabel } from './format';
import { c, card, tbl } from '../../theme';

// The company's side of a billing change: four headline tiles, the change by
// company, and which charge categories it comes from.
//
// "Annualized" is the baseline month's change times twelve. That is the
// honest reading of a one-month baseline and the tile says so; it is not a
// forecast of next year's billing, which would need volumes nobody has given
// this page.
export default function ImpactSummary({ summary, periods, hasScenario, category = null, onCategory }) {
  const t = summary.totals;
  const idx = periods.findIndex((p) => p.period === summary.period);
  const prior = idx >= 0 ? periods[idx + 1] : null;
  const pct = t.current ? (t.change / t.current) * 100 : null;
  const moved = summary.categories.filter((x) => Math.abs(x.change) >= 0.005);

  return (
    <div style={s.wrap}>
      <style>{css}</style>
      <div style={s.tiles}>
        <Tile label={`Billed, ${periodLabel(summary.period)}`}
              value={hasScenario ? <><span style={s.from}>{fmtCompact(t.current)} →</span> {fmtCompact(t.projected)}</> : fmtCompact(t.current)}
              sub={hasScenario
                ? <span style={{ color: t.change >= 0 ? c.successText : c.dangerText }}>{fmtCompact(t.change, { signed: true })} per month ({fmtPct(pct)})</span>
                : prior && !summary.company ? `${fmtPct(((t.current - prior.amount) / prior.amount) * 100)} vs ${periodLabel(prior.period, { short: true })}` : 'Pre-tax, all charges'} />
        <Tile label="Annualized company impact"
              value={hasScenario ? fmtCompact(t.change * 12, { signed: true }) : '—'}
              sub={`${periodLabel(summary.period, { short: true })} change × 12`} />
        <Tile label="Customers affected"
              value={hasScenario ? fmtNumber(t.affected) : '—'}
              sub={`of ${fmtNumber(t.accounts)} billed${hasScenario && t.decreased ? `; ${fmtNumber(t.decreased)} go down` : ''}`} />
        <Tile label="Typical customer"
              value={hasScenario ? fmtChange(t.median_change) : '—'}
              sub="Median monthly change" />
      </div>

      <div className="cb-impact-grid">
        <section style={s.card}>
          <div style={s.cardHead}>Impact by company</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Company</th>
                  <th style={s.thR}>Customers</th>
                  <th style={s.thR}>Billed</th>
                  <th style={s.thR}>Projected</th>
                  <th style={s.thR}>Monthly change</th>
                  <th style={s.thR}>Annualized</th>
                </tr>
              </thead>
              <tbody>
                {summary.companies.map((r) => (
                  <tr key={r.company} style={tbl.tr}>
                    <td style={{ ...s.td, fontWeight: 600 }}>{r.company}</td>
                    <td style={s.tdR}>{fmtNumber(r.accounts)}</td>
                    <td style={s.tdR}>{fmtMoney(r.current)}</td>
                    <td style={s.tdR}>{fmtMoney(r.projected)}</td>
                    <td style={{ ...s.tdR, ...changeColor(r.change) }}>
                      {fmtChange(r.change)} <span style={s.muted}>{r.current ? fmtPct((r.change / r.current) * 100) : ''}</span>
                    </td>
                    <td style={s.tdR}>{fmtChange(r.change * 12)}</td>
                  </tr>
                ))}
                <tr style={s.totalRow}>
                  <td style={{ ...s.td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...s.tdR, fontWeight: 700 }}>{fmtNumber(t.accounts)}</td>
                  <td style={{ ...s.tdR, fontWeight: 700 }}>{fmtMoney(t.current)}</td>
                  <td style={{ ...s.tdR, fontWeight: 700 }}>{fmtMoney(t.projected)}</td>
                  <td style={{ ...s.tdR, fontWeight: 700, ...changeColor(t.change) }}>{fmtChange(t.change)}</td>
                  <td style={{ ...s.tdR, fontWeight: 700 }}>{fmtChange(t.change * 12)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section style={s.card}>
          <div style={{ ...s.cardHead, display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
            <span>By charge category, {periodLabel(summary.period, { short: true })}</span>
            <span style={s.headHint}>Click a category to list its customers</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Category</th>
                  <th style={s.thR}>Customers</th>
                  <th style={s.thR}>Billed</th>
                  <th style={s.thR}>Change</th>
                </tr>
              </thead>
              <tbody>
                {/* Each category is a report of its own: the row opens the
                    customers who were billed it, with their amount for it. */}
                {summary.categories.map((r) => {
                  const on = category === r.category;
                  return (
                    <tr key={r.category} style={{ ...tbl.tr, ...(on ? s.rowOn : null) }}>
                      <td style={s.td}>
                        {r.retired ? (
                          <span style={s.retired}>{r.label}</span>
                        ) : (
                          <button type="button" className="cb-cat" aria-pressed={on} onClick={() => onCategory?.(on ? null : r.category)}>
                            {r.label}
                          </button>
                        )}
                      </td>
                      <td style={s.tdR}>{fmtNumber(r.accounts)}</td>
                      <td style={s.tdR}>{fmtMoney(r.current)}</td>
                      <td style={{ ...s.tdR, ...changeColor(r.change) }}>{fmtChange(r.change)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {summary.categories.some((r) => r.retired) && (
            <div style={s.retiredNote} role="note">
              Some lines are filed under a category that no longer exists: they were loaded before the category
              rules changed. They are counted in every total but cannot be split out until a super admin presses
              Re-apply categories on the Customer Billing Upload page.
            </div>
          )}
          {hasScenario && !moved.length && (
            <div style={s.foot}>This scenario changes no category that was billed this month.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function Tile({ label, value, sub }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue}>{value}</div>
      <div style={s.tileSub}>{sub}</div>
    </div>
  );
}

// Side by side only on a wide screen: the company table has six columns and
// the category table four, and at laptop width either one squeezed beside the
// other cuts off its change column.
const css = `
.cb-cat {
  background: none; border: 0; padding: 0; font: inherit; color: ${c.info};
  cursor: pointer; text-align: left;
}
.cb-cat:hover { text-decoration: underline; }
.cb-cat[aria-pressed="true"] { color: ${c.text}; font-weight: 600; }
.cb-impact-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; }
@media (max-width: 1700px) { .cb-impact-grid { grid-template-columns: minmax(0, 1fr); } }
`;

const changeColor = (n) => (n >= 0.005 ? { color: c.successText } : n <= -0.005 ? { color: c.dangerText } : { color: c.textFaint });

const s = {
  wrap:      { marginBottom: '14px' },
  tiles:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' },
  tile:      { ...card.sm, padding: '12px 14px' },
  tileLabel: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  tileValue: { fontSize: '20px', fontWeight: 700, color: c.text, lineHeight: 1.2, marginTop: '4px', fontVariantNumeric: 'tabular-nums' },
  from:      { fontSize: '13px', fontWeight: 500, color: c.textMuted },
  tileSub:   { fontSize: '11px', color: c.textMuted, marginTop: '3px', lineHeight: 1.35 },
  card:      { ...card.sm, overflow: 'hidden' },
  cardHead:  { ...card.head, padding: '11px 14px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:        { ...tbl.thSm, whiteSpace: 'nowrap' },
  thR:       { ...tbl.thRSm },
  td:        { ...tbl.tdSm, whiteSpace: 'nowrap' },
  tdR:       { ...tbl.tdRSm },
  muted:     { color: c.textFaint, fontWeight: 400, fontSize: '11px' },
  totalRow:  { background: c.surfaceSunk, borderTop: `1px solid ${c.border}` },
  headHint:  { fontSize: '11px', fontWeight: 400, color: c.textFaint },
  rowOn:     { background: c.infoTint },
  retired:   { color: c.warningText, fontStyle: 'italic' },
  retiredNote: { padding: '10px 14px', fontSize: '11.5px', color: c.warningText, background: c.warningTint, borderTop: `1px solid ${c.border}` },
  foot:      { padding: '10px 14px', fontSize: '11.5px', color: c.textMuted, borderTop: `1px solid ${c.border}` },
};
